import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { Loader2, Plus, RefreshCw, Search } from "lucide-react";
import {
  useStateMachine,
  useStateMachineValue,
} from "@/core/arch/state_machine";
import { useAdminApp } from "@/hooks/useAdminApp";
import { AdminsRepository } from "@/application/admins/admins_repository";
import { AdminsListBloc } from "@/application/admins/admins_list_bloc";
import type { AdminUser } from "@/application/admins/types";
import { DataTable } from "@/components/ui/data-table/DataTable";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/base/dialog";
import { Label } from "@/components/ui/base/label";
import { buildAdminsColumns } from "./AdminsColumns";

// ── Create Admin form schema ─────────────────────────────────────────

const createAdminSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .regex(/@cpos\.com$/i, "Must be a @cpos.com email address"),
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(50, "First name must be 50 characters or less"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(50, "Last name must be 50 characters or less"),
});

type CreateAdminFormValues = z.infer<typeof createAdminSchema>;

// ── Toolbar ──────────────────────────────────────────────────────────

function AdminsToolbar({
  search,
  onSearchChange,
  onCreateClick,
  onSyncClick,
  syncing,
  syncNeeded,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  onCreateClick: () => void;
  onSyncClick: () => void;
  syncing: boolean;
  syncNeeded: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="relative">
        <Search className="text-subtle-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input
          placeholder="Search by email or name..."
          className="w-64 pl-9"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <div className="flex items-center gap-2">
        {syncNeeded && (
          <Button
            size="sm"
            variant="outline"
            disabled={syncing}
            onClick={onSyncClick}
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Sync Google Logins
          </Button>
        )}
        <Button size="sm" onClick={onCreateClick}>
          <Plus className="h-4 w-4" />
          Create Admin
        </Button>
      </div>
    </div>
  );
}

// ── Create Admin dialog ───────────────────────────────────────────────

function CreateAdminDialog({
  open,
  submitting,
  onClose,
  onSubmit,
}: {
  open: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: CreateAdminFormValues) => Promise<void>;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateAdminFormValues>({
    resolver: zodResolver(createAdminSchema),
  });

  // Radix's onOpenChange only fires on user-initiated closes, so a parent-driven
  // close (e.g. after a successful submit) wouldn't trigger reset. Reset whenever
  // the controlled open transitions to false.
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Admin</DialogTitle>
          <DialogDescription>
            Add a @cpos.com user to the CPOS-Admin group. They can sign in via
            Google.
          </DialogDescription>
        </DialogHeader>

        <form
          id="create-admin-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@cpos.com"
              {...register("email")}
              aria-invalid={!!errors.email}
            />
            {errors.email ? (
              <p className="text-xs text-red-500">{errors.email.message}</p>
            ) : (
              <p className="text-muted-foreground text-xs">
                Admin must already exist in Google Workspace.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              placeholder="First name"
              {...register("firstName")}
              aria-invalid={!!errors.firstName}
            />
            {errors.firstName && (
              <p className="text-xs text-red-500">{errors.firstName.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              placeholder="Last name"
              {...register("lastName")}
              aria-invalid={!!errors.lastName}
            />
            {errors.lastName && (
              <p className="text-xs text-red-500">{errors.lastName.message}</p>
            )}
          </div>
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form="create-admin-form" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Deactivate confirm dialog ─────────────────────────────────────────

function DeactivateDialog({
  target,
  submitting,
  onClose,
  onConfirm,
}: {
  target: AdminUser | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  return (
    <Dialog open={target !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deactivate Admin</DialogTitle>
          <DialogDescription>
            Deactivate <strong>{target?.email}</strong>? They will be signed out
            and removed from the CPOS-Admin group. This is reversible by an
            engineer.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Deactivate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── errorCode → user-facing message ──────────────────────────────────

const ERROR_CODE_MESSAGES: Record<string, string> = {
  WORKSPACE_USER_NOT_FOUND:
    "User not found in Google Workspace. Confirm the email and try again.",
  WORKSPACE_USER_SUSPENDED: "This Google Workspace user is suspended.",
  WORKSPACE_USER_ARCHIVED: "This Google Workspace user is archived.",
  WORKSPACE_LOOKUP_FAILED: "Workspace lookup failed. Please try again.",
  INVALID_EMAIL_DOMAIN: "Email must be a @cpos.com address.",
  INVALID_INPUT: "First name and last name are required.",
  COGNITO_OPERATION_FAILED: "Could not create admin. Please try again.",
};

function resolveCreateErrorMessage(
  errorCode: string | null,
  errors: string[],
): string {
  // eslint-disable-next-line security/detect-object-injection
  const mapped = errorCode ? ERROR_CODE_MESSAGES[errorCode] : undefined;
  return mapped ?? errors[0] ?? "Could not create admin.";
}

// ── AdminsView ────────────────────────────────────────────────────────

export function AdminsView() {
  const { adminUserBloc } = useAdminApp();
  const [authState] = useStateMachineValue(adminUserBloc);
  const currentUserEmail = authState.email;

  const [state, bloc] = useStateMachine(
    () => new AdminsListBloc(new AdminsRepository()),
  );

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDeactivate, setPendingDeactivate] = useState<AdminUser | null>(
    null,
  );

  useEffect(() => {
    void bloc.load();
  }, [bloc]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return state.items;
    const q = search.toLowerCase();
    return state.items.filter(
      (item) =>
        item.email.toLowerCase().includes(q) ||
        `${item.firstName} ${item.lastName}`.toLowerCase().includes(q),
    );
  }, [state.items, search]);

  const columns = useMemo(
    () =>
      buildAdminsColumns(currentUserEmail, (row) => setPendingDeactivate(row)),
    [currentUserEmail],
  );

  async function handleCreate(values: CreateAdminFormValues) {
    const result = await bloc.createAdmin(values);
    if (result.success) {
      setCreateOpen(false);
      const msg =
        result.mode === "promoted" || result.mode === "linked"
          ? `Existing user ${values.email} linked and promoted to admin.`
          : `Admin ${values.email} created. They can now sign in via Google.`;
      toast.success(msg);
    } else {
      toast.error(resolveCreateErrorMessage(result.errorCode, result.errors));
    }
  }

  async function handleSyncGoogleLogins() {
    const result = await bloc.syncGoogleIdentities();
    const linked = result.linkedEmails.length;
    if (result.errors.length > 0) {
      toast.error(
        linked > 0
          ? `Linked ${linked} admin(s), but some failed: ${result.errors.join("; ")}`
          : `Sync failed: ${result.errors.join("; ")}`,
      );
    } else if (linked > 0) {
      toast.success(
        `Linked Google logins for ${result.linkedEmails.join(", ")}.`,
      );
    } else {
      toast.success("All admins already have a Google login.");
    }
  }

  async function handleDeactivateConfirm() {
    if (!pendingDeactivate) return;
    const result = await bloc.deactivateAdmin(pendingDeactivate.sub);
    if (result.success) {
      setPendingDeactivate(null);
      toast.success(`${pendingDeactivate.email} has been deactivated.`);
    } else {
      toast.error(result.errors.join(", ") || "Failed to deactivate admin");
    }
  }

  const isLoading = state.status === "loading" && state.items.length === 0;
  const isSubmitting = state.pendingAction !== null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-foreground text-2xl font-bold">Admins</h1>
      </div>

      {isLoading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
        </div>
      ) : state.status === "error" ? (
        <div className="bg-card rounded-lg border px-4 py-8 text-center text-sm text-red-500">
          Failed to load admins: {state.error}
        </div>
      ) : state.status === "loaded" && state.items.length === 0 ? (
        <div className="bg-card text-muted-foreground flex flex-col items-center gap-4 rounded-lg border px-4 py-16 text-center text-sm">
          <p>No admins yet — add the first one.</p>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            Create Admin
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filteredItems}
          initialSorting={[{ id: "createdAt", desc: true }]}
          toolbar={() => (
            <AdminsToolbar
              search={search}
              onSearchChange={setSearch}
              onCreateClick={() => setCreateOpen(true)}
              onSyncClick={() => void handleSyncGoogleLogins()}
              syncing={state.pendingAction === "sync"}
              syncNeeded={state.items.some(
                (item) => !item.hasGoogleLogin && item.status === "active",
              )}
            />
          )}
        />
      )}

      <CreateAdminDialog
        open={createOpen}
        submitting={isSubmitting}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <DeactivateDialog
        target={pendingDeactivate}
        submitting={isSubmitting}
        onClose={() => setPendingDeactivate(null)}
        onConfirm={handleDeactivateConfirm}
      />
    </div>
  );
}
